/**
 * Logging wrapper.
 * Strips PII before sending to error trackers.
 */

import { config } from './config';

const PII_PATTERNS = [
  /Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/g, // JWT
  /\b\d{3}-\d{3}-\d{4}\b/g, // phone
  /\b[\w._%+-]+@[\w.-]+\.[a-z]{2,}\b/gi, // email
];

export function scrub(s: string): string {
  return PII_PATTERNS.reduce((acc, re) => acc.replace(re, '[REDACTED]'), s);
}

type Level = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<Level, number> = { debug: 10, info: 20, warn: 30, error: 40 };

// Threshold at/above which a message reaches the console. An explicit
// VITE_LOG_LEVEL wins (so a deployed env can be turned up to 'debug' to
// diagnose issues without a rebuild); otherwise verbose in dev, warnings +
// errors in prod. Previously emit() was gated on `config.isDev` alone, so
// production builds silently dropped EVERY log — including errors — which is
// why MQTT connection failures left no trace (ClickUp 86e1yvx21).
function resolveThreshold(): number {
  const explicit = config.logLevel.toLowerCase() as Level;
  if (explicit in LEVEL_ORDER) return LEVEL_ORDER[explicit];
  return config.isDev ? LEVEL_ORDER.debug : LEVEL_ORDER.warn;
}

const threshold = resolveThreshold();

type LogSink = (msg: string, ctx?: Record<string, unknown>) => void;

// External sinks for error-level logs, wired to Sentry (`lib/sentry.ts`) and
// Faro (`lib/faro.ts`) via registerErrorSink(). Kept as an indirection so THIS
// module never imports those SDKs — log.ts is pulled into the MQTT source
// chain, and neither browser SDK must leak into that (worker) bundle. Multiple
// sinks so both trackers receive errors while we run them side by side.
const errorSinks: LogSink[] = [];

export function registerErrorSink(sink: LogSink): void {
  errorSinks.push(sink);
}

function emit(level: Level, msg: string, ctx?: Record<string, unknown>) {
  if (LEVEL_ORDER[level] >= threshold) {
    console[level === 'debug' ? 'log' : level](`[${level}] ${msg}`, ctx ?? '');
  }
  // Errors always reach the sinks (Sentry / Faro) regardless of the console
  // threshold. `msg` is already PII-scrubbed by log.error() before emit().
  if (level === 'error') for (const sink of errorSinks) sink(msg, ctx);
}

export const log = {
  debug: (msg: string, ctx?: Record<string, unknown>) => emit('debug', msg, ctx),
  info: (msg: string, ctx?: Record<string, unknown>) => emit('info', msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) => emit('warn', msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) => emit('error', scrub(msg), ctx),
};
